import { useCallback } from "react";
import {
  Badge,
  Box,
  EmptyState,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@salestrak/ui";
import { SetIntegrationProviderFeaturedDocument } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  PanelCard,
  ScreenHeader,
  TableRowsSkeleton,
} from "./admin-ui";
import { useAdminFetch } from "./admin-shared";
import type { DbProvider } from "./use-admin-data";

// Immediate-persist toggle mutation for a provider's `featured` flag — one
// request per flip (no save button), mirroring useFlagMutations.
function useIntegrationMutations(refetchProviders: () => void) {
  const { coreGql } = useAdminFetch();
  const setFeatured = useCallback(
    async (provider: DbProvider, featured: boolean) => {
      try {
        await coreGql(SetIntegrationProviderFeaturedDocument, {
          input: { provider: provider.slug, featured },
        });
        refetchProviders();
      } catch (err) {
        toastMutationError(err, "Failed to update integration");
      }
    },
    [coreGql, refetchProviders],
  );
  return { setFeatured };
}

function ProviderRow({
  provider,
  onToggle,
}: {
  provider: DbProvider;
  onToggle: (featured: boolean) => void;
}) {
  const count = provider.connectionCount;
  return (
    <TableRow>
      <TableCell>
        <Box>
          <Box align="center" direction="row" gap="xs">
            <Text size="sm" truncate={1} weight="semibold">
              {provider.displayName}
            </Text>
            <Badge color="info">
              <Text mono size="xs">
                {provider.kind}
              </Text>
            </Badge>
          </Box>
          <Text mono size="2xs" truncate={1} variant="muted">
            {provider.slug}
          </Text>
        </Box>
      </TableCell>
      <TableCell>
        <Text size="sm" variant="muted">
          {count} {count === 1 ? "connection" : "connections"}
        </Text>
      </TableCell>
      <TableCell>
        <Box align="center" direction="row" gap="xs">
          <Switch checked={provider.featured} onCheckedChange={onToggle} />
          <Text size="xs" variant={provider.featured ? "success" : "muted"} weight="medium">
            {provider.featured ? "Featured" : "Off"}
          </Text>
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function IntegrationsScreen({
  providers,
  loading,
  refetchProviders,
}: {
  providers: DbProvider[];
  loading: boolean;
  refetchProviders: () => void;
}) {
  const { setFeatured } = useIntegrationMutations(refetchProviders);
  const featuredCount = providers.filter((p) => p.featured).length;

  return (
    <Box fill minHeight={0}>
      <ScreenHeader
        meta={`${providers.length} providers · ${featuredCount} featured`}
        title="Integrations"
      />
      <Box fill overflowY="auto">
        <Box paddingBottom="xl" paddingTop="sm" paddingX="lg">
          <PanelCard>
            {/* `table-fixed`: the px columns keep their width and the unsized
                one takes what is left, and a bounded cell is what lets a long
                provider name ellipsize instead of widening the table. */}
            <Table layout="fixed">
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead width={140}>Connections</TableHead>
                  <TableHead width={120}>Featured</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && providers.length === 0 ? (
                  <TableRowsSkeleton
                    rows={4}
                    columns={[{ fill: true }, { width: 140 }, { width: 120 }]}
                  />
                ) : null}

                {providers.map((provider, index) => (
                  <ProviderRow
                    key={`${provider.slug}-${index}`}
                    provider={provider}
                    onToggle={(featured) => setFeatured(provider, featured)}
                  />
                ))}
              </TableBody>
            </Table>

            {!loading && providers.length === 0 ? (
              <EmptyState size="xs" title="No integration providers found" />
            ) : null}
          </PanelCard>
        </Box>
      </Box>
    </Box>
  );
}
